import React, { useContext, useEffect, useState } from "react";
import {
    Container,
    Card,
    CardHeader,
    CardBody,
    Row,
    Col,
    Form,
    FormGroup,
    Label,
    Input,
    Button,
    Table,
    Badge,
    Spinner,
} from "reactstrap";
import Select from "react-select";
import Header from "../../components/Headers/Header";
import AdminNavbar from "../../components/Navbars/AdminNavbar/AdminNavbar";
import { AuthContext } from "../../context/AuthContext";
import { InstituicaoContext } from "../../context/InstituicaoContext";
import { useToast } from "../../context/ToastContext";
import api from "../../api";

interface InstituicaoAnoMetricaDTO {
    ano: number;
    quantidadeContratos: number;
    quantidadeFormandos: number;
    quantidadeVendas: number;
    valorTotalVendido: number;
    ticketMedio: number;
}

interface ComparativoAnualInstituicaoDTO {
    instituicaoId: number;
    instituicaoNome: string;
    cidade: string;
    estado: string;
    anos: Record<number, InstituicaoAnoMetricaDTO>;
}

interface ComparativoAnualVendasResponseDTO {
    anos: number[];
    instituicoes: ComparativoAnualInstituicaoDTO[];
    totaisPorAno: Record<number, InstituicaoAnoMetricaDTO>;
}

const ComparativoAnualVendas: React.FC = () => {
    const showToast = useToast();
    const { objUserTk } = useContext(AuthContext);
    const { listInstituicao, handleGetInstituicaoByEmpresaId } = useContext(InstituicaoContext);

    const currentYear = new Date().getFullYear();
    const [selectedYears, setSelectedYears] = useState<number[]>([currentYear - 1, currentYear]);
    const [selectedInstituicaoId, setSelectedInstituicaoId] = useState<number | undefined>(undefined);
    const [loading, setLoading] = useState<boolean>(false);
    const [exportingPdf, setExportingPdf] = useState<boolean>(false);
    const [data, setData] = useState<ComparativoAnualVendasResponseDTO | null>(null);

    // Lista de anos para seleção (últimos 10 anos + próximos 2)
    const yearOptions = Array.from({ length: 12 }, (_, i) => currentYear - 8 + i).map((y) => ({
        value: y,
        label: `Ano de Finalização: ${y}`,
    }));

    useEffect(() => {
        if (objUserTk?.EmpresaId) {
            handleGetInstituicaoByEmpresaId(objUserTk.EmpresaId);
            fetchComparativo([currentYear - 1, currentYear], undefined);
        }
    }, [objUserTk?.EmpresaId]);

    const fetchComparativo = async (anos: number[], instId?: number) => {
        if (!objUserTk?.EmpresaId || anos.length === 0) return;
        setLoading(true);
        try {
            const params = new URLSearchParams();
            params.append("empresaId", objUserTk.EmpresaId.toString());
            anos.forEach((ano) => params.append("anos", ano.toString()));
            if (instId) params.append("instituicaoId", instId.toString());

            const res = await api.get<any>("/Contrato/comparativo-anual", {
                params,
            });
            const raw = res.data || {};
            const anosNorm = raw.anos || raw.anosConsultados || anos || [];
            const instituicoesNorm = raw.instituicoes || [];
            
            // Reconstroi totaisPorAno se vier como totaisGeraisPorAno ou vazio
            const totaisNorm: Record<number, InstituicaoAnoMetricaDTO> = raw.totaisPorAno || {};
            anosNorm.forEach((ano: number) => {
                if (!totaisNorm[ano]) {
                    let totalVendido = raw.totaisGeraisPorAno && raw.totaisGeraisPorAno[ano] !== undefined
                        ? raw.totaisGeraisPorAno[ano]
                        : 0;
                    let totalContratos = 0;
                    let totalVendas = 0;
                    let totalFormandos = 0;

                    instituicoesNorm.forEach((inst: any) => {
                        const m = inst.anos && inst.anos[ano];
                        if (m) {
                            totalContratos += m.quantidadeContratos || 0;
                            totalVendas += m.quantidadeVendas || 0;
                            totalFormandos += m.quantidadeAlunos || m.quantidadeFormandos || 0;
                            if (!raw.totaisGeraisPorAno) totalVendido += m.valorTotalVendido || 0;
                        }
                    });

                    totaisNorm[ano] = {
                        ano,
                        quantidadeContratos: totalContratos,
                        quantidadeFormandos: totalFormandos,
                        quantidadeVendas: totalVendas,
                        valorTotalVendido: totalVendido,
                        ticketMedio: totalFormandos > 0 ? Math.round(totalVendido / totalFormandos) : 0,
                    };
                }
            });

            setData({
                anos: anosNorm,
                instituicoes: instituicoesNorm,
                totaisPorAno: totaisNorm,
            });
        } catch (err) {
            console.error("Erro ao carregar comparativo:", err);
            showToast("error", "Erro ao carregar o comparativo anual de vendas.");
        } finally {
            setLoading(false);
        }
    };

    const handleSearch = (e: React.FormEvent) => {
        e.preventDefault();
        if (selectedYears.length === 0) {
            showToast("warning", "Selecione ao menos um ano de finalização para comparar.");
            return;
        }
        fetchComparativo(selectedYears, selectedInstituicaoId);
    };

    const handleExportPdf = async () => {
        if (!objUserTk?.EmpresaId || selectedYears.length === 0) return;
        setExportingPdf(true);
        try {
            const params = new URLSearchParams();
            params.append("empresaId", objUserTk.EmpresaId.toString());
            selectedYears.forEach((ano) => params.append("anos", ano.toString()));
            if (selectedInstituicaoId) params.append("instituicaoId", selectedInstituicaoId.toString());

            const res = await api.get("/Contrato/comparativo-anual/export/pdf", {
                params,
                responseType: "blob",
            });
            const blob = new Blob([res.data], { type: "application/pdf" });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", `comparativo-anual-vendas-${selectedYears.join("-")}.pdf`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (err) {
            console.error("Erro ao exportar PDF:", err);
            showToast("error", "Erro ao exportar PDF do comparativo anual.");
        } finally {
            setExportingPdf(false);
        }
    };

    const formatCurrency = (val?: number) => {
        return (val || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
    };

    const calcVariacao = (valAtual?: number, valAnterior?: number) => {
        if (!valAnterior || valAnterior === 0) {
            if (!valAtual || valAtual === 0) return null;
            return "+100%";
        }
        const diff = ((valAtual || 0) - valAnterior) / valAnterior * 100;
        const sinal = diff > 0 ? "+" : "";
        return `${sinal}${diff.toFixed(1)}%`;
    };

    return (
        <>
            <Header />
            <AdminNavbar
                brandText="Comparativo Anual de Vendas por Instituição"
                search={false}
                titleSearch=""
            />
            <Container className="mt--7" fluid>

                <Card className="shadow border-0 mb-4">
                    <CardHeader className="bg-transparent border-0 pb-0">
                        <Row className="align-items-center">
                            <Col md={8}>
                                <h3 className="mb-0 text-primary">
                                    <i className="fa-solid fa-chart-line mr-2"></i> Comparativo Anual de Vendas por Instituição
                                </h3>
                                <p className="text-muted small mb-0">
                                    Comparativo de vendas por <strong>ano de conclusão/encerramento</strong> do contrato (ex: contratos finalizados em 2025 vs 2026).
                                </p>
                            </Col>
                            <Col md={4} className="text-right">
                                <Button
                                    color="danger"
                                    onClick={handleExportPdf}
                                    disabled={loading || exportingPdf || !data || !data.instituicoes || data.instituicoes.length === 0}
                                >
                                    {exportingPdf ? (
                                        <>
                                            <Spinner size="sm" className="mr-1" /> Exportando...
                                        </>
                                    ) : (
                                        <>
                                            <i className="fa-solid fa-file-pdf mr-1"></i> Exportar PDF
                                        </>
                                    )}
                                </Button>
                            </Col>
                        </Row>
                    </CardHeader>
                    <CardBody>
                        <Form onSubmit={handleSearch} className="mb-4">
                            <Row className="align-items-end">
                                <Col md={5}>
                                    <FormGroup className="mb-0">
                                        <Label className="font-weight-bold small text-muted">
                                            Anos de Finalização do Contrato
                                        </Label>
                                        <Select
                                            isMulti
                                            options={yearOptions}
                                            value={yearOptions.filter((o) => selectedYears.includes(o.value))}
                                            onChange={(selected) => {
                                                const vals = (selected as any[] || []).map((s) => s.value);
                                                setSelectedYears(vals);
                                            }}
                                            placeholder="Selecione os anos para comparar..."
                                            closeMenuOnSelect={false}
                                        />
                                    </FormGroup>
                                </Col>
                                <Col md={4}>
                                    <FormGroup className="mb-0">
                                        <Label className="font-weight-bold small text-muted">
                                            Instituição de Ensino (Opcional)
                                        </Label>
                                        <Input
                                            type="select"
                                            value={selectedInstituicaoId || ""}
                                            onChange={(e) =>
                                                setSelectedInstituicaoId(e.target.value ? Number(e.target.value) : undefined)
                                            }
                                        >
                                            <option value="">Todas as Instituições</option>
                                            {(listInstituicao || []).map((inst: any) => (
                                                <option key={inst.id} value={inst.id}>
                                                    {inst.name} {inst.city ? `(${inst.city}/${inst.state})` : ""}
                                                </option>
                                            ))}
                                        </Input>
                                    </FormGroup>
                                </Col>
                                <Col md={3}>
                                    <Button color="primary" type="submit" block disabled={loading}>
                                        <i className="fa-solid fa-magnifying-glass mr-1"></i> Filtrar Comparativo
                                    </Button>
                                </Col>
                            </Row>
                        </Form>

                        {/* CARDS COM RESUMO DOS ANOS SELECIONADOS */}
                        {data && data.anos && data.anos.length > 0 && (
                            <Row className="mb-4">
                                {data.anos.map((ano, idx) => {
                                    const metrica = data.totaisPorAno[ano] || {
                                        quantidadeContratos: 0,
                                        quantidadeFormandos: 0,
                                        quantidadeVendas: 0,
                                        valorTotalVendido: 0,
                                        ticketMedio: 0,
                                    };
                                    const anoAnterior = idx > 0 ? data.anos[idx - 1] : null;
                                    const metricaAnterior = anoAnterior ? data.totaisPorAno[anoAnterior] : null;
                                    const variacao = metricaAnterior
                                        ? calcVariacao(metrica.valorTotalVendido, metricaAnterior.valorTotalVendido)
                                        : null;

                                    return (
                                        <Col key={ano} md={Math.max(3, Math.floor(12 / data.anos.length))}>
                                            <Card className="border-0 shadow-sm" style={{ borderLeft: "4px solid #5e72e4" }}>
                                                <CardBody className="p-3">
                                                    <div className="d-flex justify-content-between align-items-center mb-1">
                                                        <h5 className="mb-0 font-weight-bold text-primary">Finalização {ano}</h5>
                                                        {variacao && (
                                                            <Badge color={variacao.startsWith("+") ? "success" : "danger"} pill>
                                                                {variacao} vs {anoAnterior}
                                                            </Badge>
                                                        )}
                                                    </div>
                                                    <h3 className="font-weight-bold text-dark mb-1">
                                                        {formatCurrency(metrica.valorTotalVendido)}
                                                    </h3>
                                                    <div className="small text-muted">
                                                        <span>{metrica.quantidadeContratos} contratos</span> •{" "}
                                                        <span>{metrica.quantidadeVendas} vendas</span> •{" "}
                                                        <span>Ticket: {formatCurrency(metrica.ticketMedio)}</span>
                                                    </div>
                                                </CardBody>
                                            </Card>
                                        </Col>
                                    );
                                })}
                            </Row>
                        )}

                        {/* TABELA COMPARATIVA DETALHADA POR INSTITUIÇÃO */}
                        {loading ? (
                            <div className="text-center py-5">
                                <Spinner color="primary" />
                                <p className="mt-2 text-muted">Processando métricas anuais por instituição...</p>
                            </div>
                        ) : !data || !data.instituicoes || data.instituicoes.length === 0 ? (
                            <div className="text-center py-5 text-muted">
                                <i className="fa-solid fa-folder-open fa-3x mb-3 text-light"></i>
                                <p>Nenhum contrato com finalização nos anos selecionados foi encontrado.</p>
                            </div>
                        ) : (
                            <div className="table-responsive">
                                <Table hover size="sm" className="custom-table table-bordered mb-0" style={{ fontSize: "12px" }}>
                                    <thead className="thead-light">
                                        <tr>
                                            <th rowSpan={2} style={{ verticalAlign: "middle" }}>Instituição de Ensino</th>
                                            <th rowSpan={2} style={{ verticalAlign: "middle", width: "140px" }}>Cidade / UF</th>
                                            {(data.anos || []).map((ano) => (
                                                <th key={ano} colSpan={3} className="text-center" style={{ backgroundColor: "#e9ecef" }}>
                                                    Finalização {ano}
                                                </th>
                                            ))}
                                            {(data.anos || []).length >= 2 && (
                                                <th rowSpan={2} className="text-center" style={{ verticalAlign: "middle", width: "110px" }}>
                                                    Evolução
                                                </th>
                                            )}
                                        </tr>
                                        <tr>
                                            {(data.anos || []).map((ano) => (
                                                <React.Fragment key={ano}>
                                                    <th className="text-center" style={{ width: "70px" }}>Contr.</th>
                                                    <th className="text-center" style={{ width: "70px" }}>Vendas</th>
                                                    <th className="text-right" style={{ width: "110px" }}>Total Vendido</th>
                                                </React.Fragment>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {(data.instituicoes || []).map((inst) => {
                                            const anosArr = data.anos || [];
                                            const primeiroAno = anosArr[0];
                                            const ultimoAno = anosArr[anosArr.length - 1];
                                            const valPrimeiro = (inst.anos && inst.anos[primeiroAno]?.valorTotalVendido) || 0;
                                            const valUltimo = (inst.anos && inst.anos[ultimoAno]?.valorTotalVendido) || 0;
                                            const evolucao = anosArr.length >= 2 ? calcVariacao(valUltimo, valPrimeiro) : null;

                                            return (
                                                <tr key={inst.instituicaoId}>
                                                    <td className="font-weight-bold text-dark">{inst.instituicaoNome}</td>
                                                    <td>{inst.cidade ? `${inst.cidade}/${inst.estado}` : "-"}</td>
                                                    {anosArr.map((ano) => {
                                                        const m = inst.anos ? inst.anos[ano] : undefined;
                                                        return (
                                                            <React.Fragment key={ano}>
                                                                <td className="text-center">{m?.quantidadeContratos || 0}</td>
                                                                <td className="text-center">{m?.quantidadeVendas || 0}</td>
                                                                <td className="text-right font-weight-bold" style={{ color: (m?.valorTotalVendido || 0) > 0 ? "#2dce89" : "#8898aa" }}>
                                                                    {formatCurrency(m?.valorTotalVendido)}
                                                                </td>
                                                            </React.Fragment>
                                                        );
                                                    })}
                                                    {anosArr.length >= 2 && (
                                                        <td className="text-center font-weight-bold">
                                                            {evolucao ? (
                                                                <Badge color={evolucao.startsWith("+") ? "success" : "danger"} pill>
                                                                    {evolucao}
                                                                </Badge>
                                                            ) : (
                                                                <span className="text-muted">-</span>
                                                            )}
                                                        </td>
                                                    )}
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                    <tfoot className="font-weight-bold bg-light">
                                        <tr>
                                            <td colSpan={2} className="text-right font-weight-bold">TOTAL GERAL:</td>
                                            {(data.anos || []).map((ano) => {
                                                const tot = (data.totaisPorAno && data.totaisPorAno[ano]) || {
                                                    quantidadeContratos: 0,
                                                    quantidadeVendas: 0,
                                                    valorTotalVendido: 0,
                                                };
                                                return (
                                                    <React.Fragment key={ano}>
                                                        <td className="text-center">{tot.quantidadeContratos}</td>
                                                        <td className="text-center">{tot.quantidadeVendas}</td>
                                                        <td className="text-right text-primary font-weight-bold">
                                                            {formatCurrency(tot.valorTotalVendido)}
                                                        </td>
                                                    </React.Fragment>
                                                );
                                            })}
                                            {data.anos.length >= 2 && (
                                                <td className="text-center">
                                                    {(() => {
                                                        const p = data.totaisPorAno[data.anos[0]]?.valorTotalVendido || 0;
                                                        const u = data.totaisPorAno[data.anos[data.anos.length - 1]]?.valorTotalVendido || 0;
                                                        const totEv = calcVariacao(u, p);
                                                        return totEv ? (
                                                            <Badge color={totEv.startsWith("+") ? "success" : "danger"} pill>
                                                                {totEv}
                                                            </Badge>
                                                        ) : "-";
                                                    })()}
                                                </td>
                                            )}
                                        </tr>
                                    </tfoot>
                                </Table>
                            </div>
                        )}
                    </CardBody>
                </Card>
            </Container>
        </>
    );
};

export default ComparativoAnualVendas;
